import { useEffect, useMemo } from 'react';

import { EBroadcastTypes } from '@/types/session.types';
import StreamingCredentials from './streaming-credentials/streaming-credentials';
import IframeSettings from './iframe-settings/iframe-settings';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import getFiresideHostEmailOptions from '@/utils/get-fireside-host-email-options';
import FiresideSettings from './fireside-settings/fireside-settings';
import useKeepVideoSettingsSlidedownOpen from './helpers/use-keep-video-settings-slidedown-open';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingSession } from '@/store/slices/project/selectors';

interface VideoSettingsProps {
  isEditingInfo: boolean;
  setIsFocusedWithin?: (keepOpen: boolean) => void;
}

export default function VideoSettings({ isEditingInfo, setIsFocusedWithin }: VideoSettingsProps) {
  const session = useAppSelector(selectWorkingSession);
  const firesideHostOptions = useMemo(() => getFiresideHostEmailOptions(session?.firesides_hosts), [session]);

  const keepOpen = useKeepVideoSettingsSlidedownOpen({ session, firesideHostOptions });

  useEffect(() => {
    if (keepOpen && setIsFocusedWithin) {
      setIsFocusedWithin(true);
    }
  }, [keepOpen, setIsFocusedWithin]);

  return (
    <SlideDownCard
      autoGrow
      open={
        keepOpen ||
        !!(
          isEditingInfo &&
          session?.broadcast_type &&
          [EBroadcastTypes.rtmps, EBroadcastTypes.embed, EBroadcastTypes.fireside].includes(session.broadcast_type)
        ) ||
        (session?.broadcast_type === EBroadcastTypes.fireside && !firesideHostOptions.length)
      }
      autoOpenOnFocus
    >
      <SwitchCase input={session?.broadcast_type}>
        <Case value={EBroadcastTypes.rtmps}>
          <StreamingCredentials isEditingInfo={isEditingInfo} />
        </Case>
        <Case value={EBroadcastTypes.embed}>
          <IframeSettings isEditingInfo={isEditingInfo} />
        </Case>
        <Case value={EBroadcastTypes.fireside}>
          <FiresideSettings emails={firesideHostOptions} />
        </Case>
      </SwitchCase>
    </SlideDownCard>
  );
}
